<template>
  <CreateModal @closeCreateModal="resetForm" ref="createModal" type="announcement">
    <h-loading v-if="isAnnouncementLoading" />

    <v-form
      v-show="!isAnnouncementLoading"
      v-model="valid"
      class="mw-600 py-5 mx-auto"
      ref="announcementForm"
    >
      <v-row>
        <v-col>
          <h3 class="mt-3 mb-3">
            {{ $t('announcement.label.create.announcementType') }}
          </h3>
          <v-select
            v-model="announcement_type"
            :no-data-text="$t('appointment.filters.noData')"
            :items="announcementTypes"
            :disabled="true"
            hide-details="auto"
            item-value="value"
            item-text="name"
            class="mb-5"
            single-line
            outlined
          />
        </v-col>
      </v-row>

      <AdminServiceTypeSelect v-model="service_types" :label="$t('announcement.label.create.serviceType')" />
      <AnnouncementHostSelect
        v-model="usersToBeSend"
        :label="$t('announcement.label.create.hosts')"
        :selectedHosts="usersToBeSend"
      />
      <CitySelect v-model="cities_ids" :label="$t('announcement.label.create.cities')" />

      <v-row>
        <v-col class="pb-0">
          <h3 class="mt-3 mb-3">
            {{ $t('announcement.label.create.sendType') }}
          </h3>
          <v-select
            v-model="sendType"
            :no-data-text="$t('appointment.filters.noData')"
            :items="sendTypes"
            item-text="name"
            item-value="value"
            hide-details="auto"
            class="mb-5"
            single-line
            outlined
          />
        </v-col>
        <v-col v-if="sendType=='SCHEDULED'" cols="12">
          <v-row>
            <v-col class="pt-0">
              <SelectDate
                v-model="releaseDate"
                :label="$t('announcement.label.create.releaseDate')"
                :data="releaseDate"
                :minDate="today"
              />
            </v-col>
            <v-col class="pt-0">
              <SelectHour
                v-model="releaseHour"
                :data="releaseDate ? releaseDate : today"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <HTextInput
            v-model="title"
            :rules="[rules.required]"
            :label="$t('announcement.label.create.title')"
          />
        </v-col>
      </v-row>

      <h3 class="mb-3">
        {{ $t('announcement.label.create.message') }}
      </h3>
      <ckeditor v-if="editor" :editor="editor" v-model="content" :config="config" />

      <div class="d-flex justify-end mt-5">
        <v-btn
          @click="create"
          :loading="isLoading"
          :disabled="!valid"
          color="primary"
          elevation="0"
          large
        >
          {{ $t('create.save') }}
        </v-btn>
      </div>
    </v-form>
  </CreateModal>
</template>
<script>
import CreateModal from '@/components/app/CreateModal'
import AdminServiceTypeSelect from '@/components/AdminServiceTypeSelect'
import CitySelect from '@/components/city/CitySelect'
import HTextInput from '@/components/app/HTextInput'
import SelectDate from '@/components/date/SelectDate'
import SelectHour from '@/components/date/SelectHour'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { required } from '@/util/errorHandling'
import AnnouncementHostSelect from '@/components/announcement/AnnouncementHostSelect'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import moment from 'moment'

class Base64UploadAdapter {
  constructor(loader) {
    this.loader = loader;
  }

  upload() {
    return this.loader.file.then(file => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve({ default: reader.result });
      reader.onerror = reject;
      reader.readAsDataURL(file);
    }));
  }

  abort() {}
}

function Base64UploadAdapterPlugin(editor) {
  editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
    return new Base64UploadAdapter(loader);
  };
}

export default {
  name: 'AnnouncementCreate',

  components: {
    CreateModal,
    AdminServiceTypeSelect,
    AnnouncementHostSelect,
    CitySelect,
    HTextInput,
    SelectDate,
    SelectHour,
  },

  data() {
    return {
      editor: ClassicEditor,
      config: {
        extraPlugins: [Base64UploadAdapterPlugin],
        toolbar: {
          items: [
            'heading',
            '|',
            'bold',
            'italic',
            '|',
            'bulletedList',
            'numberedList',
            '|',
            'insertTable',
            'blockQuote',
            '|',
            'uploadImage',
            '|',
            'undo',
            'redo'
          ]
        },
        table: {
          contentToolbar: [ 'tableColumn', 'tableRow', 'mergeTableCells' ]
        },
        language: 'pt-br'
      },
      announcementTypes: [
        {name: this.$t('announcement.label.create.announcementTypes.HOST'), value: 'HOST'},
        {name: this.$t('announcement.label.create.announcementTypes.ALL'), value: 'ALL'},
        {name: this.$t('announcement.label.create.announcementTypes.CUSTOMER'), value: 'CUSTOMER'},
      ],
      sendType: 'IMMEDIATELY',
      sendTypes: [
        {name: this.$t('announcement.label.create.sendTypes.immediately'), value: 'IMMEDIATELY'},
        {name: this.$t('announcement.label.create.sendTypes.scheduled'), value: 'SCHEDULED'},
      ],
      releaseDate: null,
      releaseHour: null,
      usersToBeSend: [],
      content: null,
      announcement_type: 'HOST',
      service_types: [],
      users_ids: [],
      cities_ids: [],
      release_date: null,
      title: null,

      rules: {
        required
      },

      today: moment().format('YYYY-MM-DD'),
      valid: false,
    }
  },

  mounted() {
    this.fetchEnabledCitiesAdmin()
  },

  watch: {
    sendType(newVal) {
      if (newVal != 'SCHEDULED') {
        this.releaseDate = null
        this.releaseHour = null
      }
    },

    announcement(announcement) {
      if (!announcement?.id) return
      this.fillFields()
    }
  },

  computed: {
    ...mapGetters([
      'cities',
      'isLoading',
      'announcement',
      'isAnnouncementLoading',
    ]),
  },

  methods: {
    ...mapActions([
      'fetchEnabledCitiesAdmin',
      'fetchAnnouncements',
      'createAnnouncement',
      'showSuccessMessage',
      'showErrorMessage',
    ]),

    ...mapMutations(['setIsLoading']),

    parseDate(date) {
      if (date && date.length <= 10) return date
      return date.split('T')[0]
    },

    formatDate(date, hour) {
      const parsedDate = this.parseDate(date)

      if (parsedDate && hour) return `${parsedDate}T${hour}:00.000-0300`
      return `${parsedDate}T00:00:00.000-0300`;
    },

    async create() {
      this.setIsLoading(true)
      let formattedReleaseDate = null

      if (this.sendType == 'SCHEDULED') {
        formattedReleaseDate = this.formatDate(this.releaseDate, this.releaseHour)
      }

      if (this.usersToBeSend) {
        this.users_ids = this.usersToBeSend.map((user) => user.id)
      }

      if (this.content == null || this.content.trim() == '') {
        this.setIsLoading(false)
        this.contentErrorMessage()
        return
      }

      this.prepareContent();

      const announcement = {
        send_type: this.sendType,
        release_date: formattedReleaseDate,
        users_to_be_send: this.usersToBeSend,
        announcement_type: this.announcement_type,
        service_types: this.service_types,
        users_ids: this.users_ids,
        cities_ids: this.cities_ids,
        title: this.title,
        content: this.content,
      }

      this.createAnnouncement(announcement)
        .then(() => {
          this.resetForm()
          this.fetchAnnouncements()
          this.createSuccessMessage()
          this.$refs.createModal.closeCreateModal()
        })
        .catch((error) => {
          this.createErrorMessage(error)
        })
        .finally(() => {
          this.setIsLoading(false)
        })
    },

    prepareContent() {
      var top = "<!DOCTYPE HTML><html lang='pt-BR'><head><meta charset='UTF-8'><meta name='viewport' content='width=device-width, initial-scale=1.0'><title>"+this.title+"</title><style>@import url('https://fonts.googleapis.com/css2?family=Comfortaa&family=Roboto:wght@400;700&display=swap');/* RESET --------------------------------------------------------------- */html {box-sizing: border-box;font-size: 16px;}*, *:before, *:after {box-sizing: inherit;}body, p, ol, ul {margin: 0;padding: 0;font-weight: normal;font-family: 'Roboto', sans-serif;letter-spacing: 0.003125rem;line-height: 24px;overflow-wrap: break-word;}h1, h2, h3, h4, h5, h6 {font-family: 'Comfortaa', cursive;margin: 0;padding: 0;font-weight: 700;color: #003d58;}ol, ul {list-style: none;}blockquote {text-align: center;font-style: italic;}i {font-style: italic;}img {max-width: 100%;height: auto;}/* /RESET -------------------------------------------------------------- *//* STYLE --------------------------------------------------------------- */#messageWrapper {width: 100%;max-width: 480px;padding: 15px;font-size: 16px;line-height: 24px;color: dimgray;}#messageWrapper h1 {margin: 0 0 15px;font-size: 36px;line-height: 46px;}#messageWrapper h2 {margin: 15px 0;font-size: 28px;font-weight: 700;line-height: 38px;}#messageWrapper h3 {margin: 15px 0;font-size: 22px;font-weight: 700;line-height: 32px;}#messageWrapper p {margin: 0 0 15px;}#messageWrapper a {color:#007aaa;font-weight: 700;}#messageWrapper ul,#messageWrapper ol {padding: 0 0 0 30px;margin: 0 0 15px;}#messageWrapper ul {list-style-type: disc;}#messageWrapper ol {list-style-type: decimal;}b,strong {font-weight: 700 !important;}#logoHauseful {display: block;position: relative;width: 100%;max-width: 160px;padding: 30px 0 0 0;margin: 30px auto;border-top: 1px solid #eee;}/* /STYLE -------------------------------------------------------------- */</style></head><body><div id='messageWrapper'>"
      var bottom = "</div></body></html>"
      this.content = top + this.content + bottom
    },

    resetForm() {
      this.$nextTick(() => {
        this.sendType = 'IMMEDIATELY'
        this.releaseDate = null
        this.releaseHour = null
        this.usersToBeSend = []
        this.content = null
        this.announcement_type = 'HOST'
        this.service_types = []
        this.users_ids = []
        this.cities_ids = []
        this.release_date = null
        this.title = null
      })
    },

    fillFields() {
      this.sendType = this.announcement.release_date  ? 'SCHEDULED' : 'IMMEDIATELY'
      this.releaseDate = this.announcement.release_date ? this.announcement.release_date : null
      this.releaseHour = this.getHourFromDate(this.announcement.release_date)
      this.type = this.announcement.type
      this.title = this.announcement.title
      this.content = this.clearContent(this.announcement.content)
      this.cities_ids = this.returnCitiesIds(this.announcement.cities)
      this.service_types = this.announcement.services
      this.usersToBeSend = this.announcement.hosts
      this.getHourFromDate()
    },

    returnCitiesIds(cities) {
      if (!cities.length) return
      const citiesIds = []
      cities.map((city) => citiesIds.push(city.id))
      return citiesIds
    },

    getHourFromDate(date) {
      if (!date) return null
      return date.split('T')[1].substring(0, 5)
    },

    createSuccessMessage() {
      this.showSuccessMessage(this.$t('announcement.label.messages.successCreation'))
    },

    createErrorMessage({ data }) {
      if (data.errors) {
        let errorMessage = ''
        data.errors.forEach((error) => errorMessage += `${this.$t('announcement.label.' + error.field)}: ${error.message}`)
        this.showErrorMessage(errorMessage)
      } else {
        this.showErrorMessage(data.message)
      }
    },

    contentErrorMessage() {
      this.showErrorMessage(this.$t('announcement.label.create.validation.content'))
    },

    clearContent(content) {
      if (!content) return
      const parsedContent = content.split("<div id='messageWrapper'>").pop().split('</div>')[0]
      return parsedContent
    },

    openModal() {
      this.$refs?.createModal?.openCreateModal()
    }
  },
}
</script>
